import array from '@mailobj-browser/front/js/utils/array.js'
import listener from '@mailobj-browser/front/js/events/listeners/listener.js'
import dragStart from '@mailobj-browser/front/js/events/types/dragStart.js'
import all from '@mailobj-browser/front/js/selectors/all.js'
import one from '@mailobj-browser/front/js/selectors/one.js'
import dragEnd from '@mailobj-browser/front/js/events/types/dragEnd.js'
import object from '@mailobj-browser/front/js/utils/object.js'
import { modifiers } from '../styles.js'
import once from '@mailobj-browser/front/js/events/options/once.js'
import stopImmediatePropagation from '@mailobj-browser/front/js/events/hooks/stopImmediatePropagation.js'
import mouseUp from '@mailobj-browser/front/js/events/types/mouseUp.js'
import { tapDown } from '@mailobj-browser/front/js/events/listeners/builtins/tap.js'
import touchEnd from '@mailobj-browser/front/js/events/types/touchEnd.js'
import touchMove from '@mailobj-browser/front/js/events/types/touchMove.js'
import touchStart from '@mailobj-browser/front/js/events/types/touchStart.js'
import touchCancel from '@mailobj-browser/front/js/events/types/touchCancel.js'
import drop from '@mailobj-browser/front/js/events/types/drop.js'
import rect from '#utilities/js/ea/rect.js'
import remove from '@mailobj-browser/front/js/tree/remove.js'
import manager from '@mailobj-browser/front/js/contracts/manager.js'
import closest from '@mailobj-browser/front/js/selectors/closest.js'
import { onDrop } from './drop.js'
import append from '@mailobj-browser/front/js/tree/append.js'
import passive from '@mailobj-browser/front/js/events/options/passive.js'

const dragging = {}

let movingElement = null
let initialsPositions = { x: 0, y: 0 }
let nativeDrag = false
const file = 'file'
const text = 'text'
const inputs = [file, text]

const noDataTransfer = object(null, {
  files: array(),
  items: array()
})

const onTapDown = object(tapDown, {
  hooks: array([
    stopImmediatePropagation
  ]),
  passive,
  task: (
    editable
  ) => {
    const draggable = editable.closest('[draggable]')

    draggable.draggable = false
    onMouseUp.listen(editable)
  }
})

const onMouseUp = object(listener, {
  type: mouseUp,
  once,
  passive,
  task: (
    editable
  ) => {
    const draggable = editable.closest('[draggable]')

    draggable.draggable = true
  }
})

const onDragStart = object(listener, {
  type: dragStart,
  passive,
  task: async (
    dragged,
    event
  ) => {
    if (!nativeDrag) {
      nativeDrag = true
    }

    const { draggable, ownerDocument = dragged } = dragged

    if (!draggable) {
      return
    }

    const { clientX, clientY, dataTransfer } = event
    const type = getType(dragged, dataTransfer)

    if (!type) {
      return
    }

    const dropZones = all(`[data-drop-accept~="${type}"]`, ownerDocument)

    for (const dropZone of dropZones) {
      if (inputs.includes(type) || !dropZone.contains(dragged)) {
        dropZone.classList.add(modifiers.drop.droppable)
      } else if (dropZone.getAttribute('data-drop-effect')) {
        dropZone.classList.add(modifiers.drop.movable)
      }
    }

    Object.assign(dragging, {
      clientX,
      clientY,
      dragged: inputs.includes(type) ?
        dataTransfer :
        dragged,
      type
    })
  }
})

const onDragEnd = object(listener, {
  type: dragEnd,
  passive,
  task: async (
    dragged
  ) => {
    const { ownerDocument } = dragged
    const { defaultView } = ownerDocument
    const { requestAnimationFrame } = defaultView
    const type = dragged.getAttribute('data-drag-type')
    const dropZones = all(`[data-drop-accept~="${type}"]`, ownerDocument)

    for (const dropZone of dropZones) {
      dropZone.classList.remove(modifiers.drop.droppable)
      dropZone.classList.remove(modifiers.drop.movable)
    }

    Object.assign(dragging, object(null, {
      dragged: null,
      type: null
    }))

    requestAnimationFrame(() => {
      dragged.draggable = true
    })
  }
})

const onTouchStart = object(listener, {
  type: touchStart,
  passive,
  task: async (
    dragged,
    event
  ) => {
    const { target, touches } = event
    const { parentNode } = dragged
    const [{ clientX, clientY }] = touches
    const { left, top } = rect(target)
    const clone = dragged.cloneNode(true)

    initialsPositions.x = clientX - left
    initialsPositions.y = clientY - top

    movingElement = clone
    await manager.fragment(clone)
    append(parentNode, clone)

    await triggerEvent(onDragStart, dragStart, event)

    onTouchMove.listen(dragged)
    onTouchEnd.listen(dragged)
    onTouchCancel.listen(dragged)
  }
})

const onTouchMove = object(listener, {
  type: touchMove,
  passive,
  task: async (
    dragged,
    event
  ) => {
    if (!movingElement) {
      return
    }

    const { changedTouches, target } = event
    const { style } = movingElement
    const [{ clientX, clientY }] = changedTouches
    const { left, top } = rect(target)

    const newX = clientX - left - initialsPositions.x
    const newY = clientY - top - initialsPositions.y

    style.setProperty('transform', `translate(${newX}px, ${newY}px`)
  }
})

const onTouchEnd = object(listener, {
  type: touchEnd,
  passive,
  task: async (
    dragged,
    event
  ) => {
    const { target, changedTouches } = event
    const [{ clientX, clientY }] = changedTouches
    const { ownerDocument } = target
    const { defaultView } = ownerDocument
    const { DragEvent } = defaultView
    const dropZone = closest('[data-drop-effect]', target)
    const { right, left, bottom, top } = rect(dropZone)
    const { style } = movingElement

    if (right < clientX || left > clientX || bottom < clientY || top > clientY) {
      style.removeProperty('transform')
      await triggerEvent(onDragEnd, dragEnd, event)
      remove(movingElement)
    } else {
      await onDrop.trigger(dropZone, new DragEvent(drop, { bubbles: true, clientX, clientY }))
      remove(dragged)
      style.removeProperty('transform')
      await triggerEvent(onDragEnd, dragEnd, event)
    }

    movingElement = null
    initialsPositions.x = 0
    initialsPositions.y = 0
  }
})

const onTouchCancel = object(listener, {
  type: touchCancel,
  passive,
  task: () => {
    remove(movingElement)
    initialsPositions.x = 0
    initialsPositions.y = 0
    movingElement = null
  }
})

const getType = (dragged, dataTransfer) => {
  const { files, items } = dataTransfer ?? noDataTransfer
  
  return files.length ?
    file :
    items.length ?
      text :
      dragged.getAttribute('data-drag-type')
}

const triggerEvent = async (listener, type, event) => {
  const { target, changedTouches } = event
  const { ownerDocument } = target
  const { defaultView } = ownerDocument
  const { DragEvent } = defaultView
  const [{ clientX, clientY }] = changedTouches

  await listener.trigger(movingElement, new DragEvent(type, { bubbles: true, clientX, clientY }))
}

export default (
  draggable
) => {
  const editable = one('input,textarea', draggable)

  if (editable) {
    onTapDown.listen(editable)
  }

  onDragStart.listen(draggable)
  onDragEnd.listen(draggable)

  if (!nativeDrag) {
    onTouchStart.listen(draggable)
  }
}

export const draggable = () => {
  return dragging
}
